import React, { useState, useRef, useEffect } from "react";
import styles from "./ShopManagement.module.css";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import { Shop, ShopPhoto } from "../../types";
import ShopPositioner2D from "../../components/ShopPositioner2D/ShopPositioner2D";

interface ShopFormData {
  nameRu: string;
  nameEn: string;
  shortDescriptionRu: string;
  shortDescriptionEn: string;
  fullDescriptionRu: string;
  fullDescriptionEn: string;
  floor: number;
  locationRu: string;
  locationEn: string;
  workingHoursRu: string;
  workingHoursEn: string;
  categoryIds: string[]; // Changed to array for multiple categories
  isActive: boolean;
  logoUrl: string;
  mapIconInactiveUrl: string;
  mapIconActiveUrl: string;
  scaleX: number;
  scaleY: number;
  positionX: number;
  positionY: number;
  positionZ: number;
  searchKeywords: string;
  instagram: string;
  facebook: string;
  twitter: string;
  tiktok: string;
  youtube: string;
  telegram: string;
  tabId: number | null;
}

export default function ShopManagement() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const mapIconInactiveInputRef = useRef<HTMLInputElement>(null);
  const mapIconActiveInputRef = useRef<HTMLInputElement>(null);
  const shopPhotosInputRef = useRef<HTMLInputElement>(null);
  const [editingShop, setEditingShop] = useState<Shop | null>(null);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [selectedTabId, setSelectedTabId] = useState<number>(2); // Всегда используем вкладку "Объекты" (tabId=2)
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [mapIconInactivePreview, setMapIconInactivePreview] = useState<
    string | null
  >(null);
  const [isUploadingInactiveIcon, setIsUploadingInactiveIcon] = useState(false);
  const [mapIconActivePreview, setMapIconActivePreview] = useState<
    string | null
  >(null);
  const [isUploadingActiveIcon, setIsUploadingActiveIcon] = useState(false);
  const [shopPhotoFiles, setShopPhotoFiles] = useState<File[]>([]);
  const [shopPhotoPreviews, setShopPhotoPreviews] = useState<string[]>([]);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [existingPhotos, setExistingPhotos] = useState<ShopPhoto[]>([]);
  const [filterName, setFilterName] = useState<string>("");
  const [filterShowInactiveOnly, setFilterShowInactiveOnly] =
    useState<boolean>(false);
  const [shopPositionerKey, setShopPositionerKey] = useState<number>(0);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [isDraggingInactiveIcon, setIsDraggingInactiveIcon] = useState(false);
  const [isDraggingActiveIcon, setIsDraggingActiveIcon] = useState(false);

  // Функция для форматирования ошибок валидации
  const formatValidationErrors = (error: any): string => {
    const errors = error.response?.data?.errors;
    if (errors && Array.isArray(errors) && errors.length > 0) {
      // Маппинг названий полей на русский язык
      const fieldNames: { [key: string]: string } = {
        "name.ru": "Название (RU)",
        "name.en": "Название (EN)",
        "workingHours.ru": "Часы работы (RU)",
        "workingHours.en": "Часы работы (EN)",
        floor: "Этаж",
        mapIconInactiveUrl: "Иконка неактивного объекта",
        mapIconActiveUrl: "Иконка активного объекта",
        categoryIds: "Категории",
        categoryId: "Категория",
        scaleX: "Масштаб X",
        scaleY: "Масштаб Y",
        positionX: "Позиция X",
        positionY: "Позиция Y",
        positionZ: "Позиция Z",
      };

      const errorMessages = errors.map((err: any) => {
        const fieldName = fieldNames[err.param] || err.param;
        let message = err.msg || "";

        // Переводы сообщений об ошибках на русский
        if (message.includes("is required and must be")) {
          message = message.replace(
            "is required and must be",
            "обязательно для заполнения и должно быть"
          );
        } else if (message.includes("is required")) {
          message = message.replace(
            "is required",
            "обязательно для заполнения"
          );
        }
        if (message.includes("cannot be empty")) {
          message = message.replace("cannot be empty", "не может быть пустым");
        }
        if (message.includes("must be a positive integer")) {
          message = "должно быть положительным числом";
        } else if (message.includes("must be an array")) {
          message = "должно быть массивом";
        } else if (message.includes("must be an object")) {
          message = message.replace(
            "must be an object",
            "должно быть объектом"
          );
        } else if (message.includes("must be")) {
          message = message.replace("must be", "должно быть");
        }
        if (message.includes("at least one category")) {
          message = "необходимо выбрать хотя бы одну категорию";
        }
        if (message.includes("must be 50 characters or less")) {
          message = "не должно превышать 50 символов";
        }
        if (message.includes("with ru and en properties")) {
          message = message.replace(
            "with ru and en properties",
            "со свойствами ru и en"
          );
        }

        return `• ${fieldName}: ${message}`;
      });

      return `Ошибки валидации:\n${errorMessages.join("\n")}`;
    }

    // Если нет детальных ошибок, возвращаем общее сообщение
    return error.response?.data?.message || "Ошибка валидации";
  };

  const initialFormData: ShopFormData = {
    nameRu: "",
    nameEn: "",
    shortDescriptionRu: "",
    shortDescriptionEn: "",
    fullDescriptionRu: "",
    fullDescriptionEn: "",
    floor: 1,
    locationRu: "",
    locationEn: "",
    workingHoursRu: "10:00 - 21:00",
    workingHoursEn: "10:00 - 21:00",
    categoryIds: [], // Changed to array
    isActive: true,
    logoUrl: "",
    mapIconInactiveUrl: "",
    mapIconActiveUrl: "",
    scaleX: 1,
    scaleY: 1,
    positionX: 0,
    positionY: 0,
    positionZ: 0,
    searchKeywords: "",
    instagram: "",
    facebook: "",
    twitter: "",
    tiktok: "",
    youtube: "",
    telegram: "",
    tabId: 2,
  };

  const [formData, setFormData] = useState<ShopFormData>(initialFormData);

  const { data: shops, isLoading: shopsLoading } = useQuery({
    queryKey: ["shops", "includeInactive"],
    queryFn: () => apiService.getShops({ includeInactive: true }),
  });

  const { data: categories } = useQuery({
    queryKey: ["categories", selectedTabId],
    queryFn: () => apiService.getCategories(selectedTabId),
    enabled: true,
  });

  const { data: floors } = useQuery({
    queryKey: ["floors"],
    queryFn: () => apiService.getFloors(),
  });

  const { data: tabs } = useQuery({
    queryKey: ["tabs"],
    queryFn: () => apiService.getTabs(),
  });

  const { data: appSettings } = useQuery({
    queryKey: ["appSettings"],
    queryFn: () => apiService.getAppSettings(),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => apiService.createShop(data),
    onSuccess: async (createdShop: any) => {
      try {
        if (shopPhotoFiles.length > 0) {
          setIsUploadingPhotos(true);
          await apiService.uploadShopPhotos(
            createdShop.id.toString(),
            shopPhotoFiles
          );
        }
      } catch (err: any) {
        setError(
          err.response?.data?.message || "Ошибка при загрузке фото объекта"
        );
      } finally {
        setIsUploadingPhotos(false);
      }
      queryClient.invalidateQueries({ queryKey: ["shops"] });
      setIsModalOpen(false);
      resetForm();
      setSuccess("Объект успешно создан!");
      setTimeout(() => setSuccess(""), 3000);
    },
    onError: (error: any) => {
      const errorMessage = formatValidationErrors(error);
      setError(errorMessage);
      // Не закрываем модальное окно при ошибке
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      apiService.updateShop(id, data),
    onSuccess: async (_: any, variables: { id: string; data: any }) => {
      try {
        if (shopPhotoFiles.length > 0) {
          setIsUploadingPhotos(true);
          await apiService.uploadShopPhotos(variables.id, shopPhotoFiles);
        }
      } catch (err: any) {
        setError(
          err.response?.data?.message || "Ошибка при загрузке фото объекта"
        );
      } finally {
        setIsUploadingPhotos(false);
      }
      queryClient.invalidateQueries({ queryKey: ["shops"] });
      setIsModalOpen(false);
      resetForm();
      setSuccess("Объект успешно обновлен!");
      setTimeout(() => setSuccess(""), 3000);
    },
    onError: (error: any) => {
      const errorMessage = formatValidationErrors(error);
      setError(errorMessage);
      // Не закрываем модальное окно при ошибке
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      console.log("[deleteMutation] mutationFn called with ID:", id);
      try {
        const result = await apiService.deleteShop(id);
        console.log("[deleteMutation] API call successful, result:", result);
        return result;
      } catch (error: any) {
        console.error("[deleteMutation] API call failed:", error);
        console.error("[deleteMutation] Error response:", error?.response);
        throw error;
      }
    },
    onSuccess: () => {
      console.log("[deleteMutation] onSuccess called");
      queryClient.invalidateQueries({ queryKey: ["shops"] });
      setSuccess("Объект успешно удален!");
      setTimeout(() => setSuccess(""), 3000);
    },
    onError: (error: any) => {
      console.error("[deleteMutation] onError called:", error);
      console.error(
        "[deleteMutation] Error response data:",
        error?.response?.data
      );
      const errorMessage =
        error.response?.data?.message || "Ошибка при удалении объекта";
      const errorDetails = error.response?.data?.details;
      // Если есть детали ошибки, добавляем их к сообщению
      const fullMessage = errorDetails
        ? `${errorMessage}. ${errorDetails}`
        : errorMessage;
      setError(fullMessage);
    },
  });

  const deletePhotoMutation = useMutation({
    mutationFn: async (photoId: string) => apiService.deleteShopPhoto(photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shops"] });
    },
  });

  const resetForm = () => {
    setFormData({
      ...initialFormData,
      workingHoursRu:
        appSettings?.workingHour?.ru || initialFormData.workingHoursRu,
      workingHoursEn:
        appSettings?.workingHour?.en || initialFormData.workingHoursEn,
    });
    setEditingShop(null);
    setError("");
    setSelectedTabId(2); // Сброс к вкладке "Объекты"
    setLogoPreview(null);
    setMapIconInactivePreview(null);
    setMapIconActivePreview(null);
    setShopPhotoFiles([]);
    setShopPhotoPreviews([]);
    setExistingPhotos([]);
    // Сбрасываем ключ компонента позиционирования для полного пересоздания
    setShopPositionerKey((prev) => prev + 1);
    // Очищаем значения input элементов файлов
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
    if (mapIconInactiveInputRef.current) {
      mapIconInactiveInputRef.current.value = "";
    }
    if (mapIconActiveInputRef.current) {
      mapIconActiveInputRef.current.value = "";
    }
    if (shopPhotosInputRef.current) {
      shopPhotosInputRef.current.value = "";
    }
  };

  const handleAddNew = () => {
    resetForm();
    setError("");
    setIsModalOpen(true);
  };

  const handleEdit = (shop: Shop) => {
    setError("");
    setEditingShop(shop);
    // Use first category's tabId for backward compatibility, or use categories if available
    const categoryTabId =
      shop.categories && shop.categories.length > 0
        ? shop.categories[0].tabId
        : shop.category?.tabId || 2;
    setSelectedTabId(shop.tabId ?? categoryTabId);
    // Extract category IDs from either categories array or single category
    const categoryIds =
      shop.categories && shop.categories.length > 0
        ? shop.categories.map((cat) => cat.id)
        : shop.category?.id
        ? [shop.category.id]
        : [];
    setFormData({
      nameRu: shop.nameRu || "",
      nameEn: shop.nameEn || "",
      shortDescriptionRu: shop.shortDescriptionRu || "",
      shortDescriptionEn: shop.shortDescriptionEn || "",
      fullDescriptionRu: shop.fullDescriptionRu || "",
      fullDescriptionEn: shop.fullDescriptionEn || "",
      floor: shop.floor,
      locationRu: shop.locationRu || "",
      locationEn: shop.locationEn || "",
      workingHoursRu: shop.workingHoursRu || appSettings?.workingHour?.ru || "",
      workingHoursEn: shop.workingHoursEn || appSettings?.workingHour?.en || "",
      categoryIds: categoryIds, // Changed to array
      isActive: shop.isActive,
      logoUrl: shop.logoUrl || "",
      mapIconInactiveUrl: shop.mapIconInactiveUrl || "",
      mapIconActiveUrl: shop.mapIconActiveUrl || "",
      scaleX: shop.scaleX,
      scaleY: shop.scaleY,
      positionX: shop.positionX,
      positionY: shop.positionY,
      positionZ: shop.positionZ,
      searchKeywords: shop.searchKeywords || "",
      instagram: shop.socialMedia?.instagram || "",
      facebook: shop.socialMedia?.facebook || "",
      twitter: shop.socialMedia?.twitter || "",
      tiktok: shop.socialMedia?.tiktok || "",
      youtube: shop.socialMedia?.youtube || "",
      telegram: shop.socialMedia?.telegram || "",
      tabId: shop.tabId ?? categoryTabId ?? 2,
    });
    setLogoPreview(null);
    setMapIconInactivePreview(
      shop.mapIconInactiveUrl
        ? `${process.env.REACT_APP_MEDIA_PATH}${shop.mapIconInactiveUrl}`
        : null
    );
    setMapIconActivePreview(
      shop.mapIconActiveUrl
        ? `${process.env.REACT_APP_MEDIA_PATH}${shop.mapIconActiveUrl}`
        : null
    );
    setExistingPhotos(shop.photos || []);
    setIsModalOpen(true);
  };

  const handleDelete = (id: string | number) => {
    const shopId = String(id);
    console.log("Attempting to delete shop with ID:", shopId);

    if (window.confirm("Вы уверены, что хотите удалить этот объект?")) {
      console.log(
        "User confirmed deletion, calling deleteMutation.mutate with ID:",
        shopId
      );
      try {
        deleteMutation.mutate(shopId);
      } catch (error) {
        console.error("Error in handleDelete:", error);
        setError("Ошибка при попытке удаления объекта");
      }
    } else {
      console.log("User cancelled deletion");
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await processLogoFile(file);
    }
  };

  const handleMapIconInactiveChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      await processInactiveIconFile(file);
    }
  };

  const handleMapIconActiveChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      await processActiveIconFile(file);
    }
  };

  // Drag and drop handlers for logo
  const handleLogoDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(true);
  };

  const handleLogoDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone itself, not a child element
    if (e.currentTarget === e.target) {
      setIsDraggingLogo(false);
    }
  };

  const handleLogoDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      await processLogoFile(file);
    }
  };

  const processLogoFile = async (file: File) => {
    setIsUploadingLogo(true);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload logo
    try {
      const result = await apiService.uploadShopLogo(file);
      setFormData({ ...formData, logoUrl: result.url });
    } catch (error: any) {
      setError(
        error.response?.data?.message || "Ошибка при загрузке логотипа"
      );
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Drag and drop handlers for inactive icon
  const handleInactiveIconDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingInactiveIcon(true);
  };

  const handleInactiveIconDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone itself, not a child element
    if (e.currentTarget === e.target) {
      setIsDraggingInactiveIcon(false);
    }
  };

  const handleInactiveIconDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingInactiveIcon(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      await processInactiveIconFile(file);
    }
  };

  const processInactiveIconFile = async (file: File) => {
    setIsUploadingInactiveIcon(true);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setMapIconInactivePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload icon
    try {
      const result = await apiService.uploadMapIconInactive(file);
      setFormData({ ...formData, mapIconInactiveUrl: result.url });
    } catch (error: any) {
      setError(
        error.response?.data?.message ||
          "Ошибка при загрузке иконки неактивного объекта"
      );
    } finally {
      setIsUploadingInactiveIcon(false);
    }
  };

  // Drag and drop handlers for active icon
  const handleActiveIconDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingActiveIcon(true);
  };

  const handleActiveIconDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone itself, not a child element
    if (e.currentTarget === e.target) {
      setIsDraggingActiveIcon(false);
    }
  };

  const handleActiveIconDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingActiveIcon(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      await processActiveIconFile(file);
    }
  };

  const processActiveIconFile = async (file: File) => {
    setIsUploadingActiveIcon(true);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setMapIconActivePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload icon
    try {
      const result = await apiService.uploadMapIconActive(file);
      setFormData({ ...formData, mapIconActiveUrl: result.url });
    } catch (error: any) {
      setError(
        error.response?.data?.message ||
          "Ошибка при загрузке иконки активного объекта"
      );
    } finally {
      setIsUploadingActiveIcon(false);
    }
  };

  const handleShopPhotosChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setShopPhotoFiles(files);
    const readers: Promise<string>[] = files.map(
      (file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        })
    );
    Promise.all(readers).then((results) => setShopPhotoPreviews(results));
  };

  const handleDeletePhoto = (photoId: string) => {
    if (window.confirm("Удалить это фото?")) {
      // Удаляем фото из локального состояния сразу
      setExistingPhotos((prev) => prev.filter((photo) => photo.id !== photoId));
      deletePhotoMutation.mutate(photoId);
    }
  };

  const handleDeleteNewPhoto = (index: number) => {
    // Удаляем фото из preview и из списка файлов
    setShopPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
    setShopPhotoFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate that at least one category is selected
    if (formData.categoryIds.length === 0) {
      setError("Необходимо выбрать хотя бы одну категорию");
      return;
    }

    // Convert form data to backend format
    const shopData = {
      name: {
        ru: formData.nameRu,
        en: formData.nameEn,
      },
      shortDescription: {
        ru: formData.shortDescriptionRu,
        en: formData.shortDescriptionEn,
      },
      fullDescription: {
        ru: formData.fullDescriptionRu,
        en: formData.fullDescriptionEn,
      },
      floor: formData.floor,
      location: {
        ru: formData.locationRu,
        en: formData.locationEn,
      },
      workingHours: {
        ru: formData.workingHoursRu,
        en: formData.workingHoursEn,
      },
      categoryIds: formData.categoryIds.map((id) => parseInt(id)),
      isActive: formData.isActive,
      searchKeywords: formData.searchKeywords,
      socialMedia: {
        instagram: formData.instagram,
        facebook: formData.facebook,
        twitter: formData.twitter,
        tiktok: formData.tiktok,
        youtube: formData.youtube,
        telegram: formData.telegram,
      },
      ...(formData.tabId ? { tabId: formData.tabId } : {}),
      scaleX: 1,
      scaleY: 1,
      positionX: formData.positionX,
      positionY: formData.positionY,
      positionZ: formData.positionZ,
      mapIconInactiveUrl: formData.mapIconInactiveUrl,
      mapIconActiveUrl: formData.mapIconActiveUrl,
      ...(formData.logoUrl && { logoUrl: formData.logoUrl }),
    };

    if (editingShop) {
      updateMutation.mutate({ id: editingShop.id, data: shopData });
    } else {
      createMutation.mutate(shopData);
    }
  };

  useEffect(() => {
    if (isModalOpen && modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [isModalOpen]);

  if (shopsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>Загрузка объектов...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.logo}>Управление объектами</div>
        <button
          className={styles.backButton}
          onClick={() => navigate("/admin")}
        >
          Назад
        </button>
      </header>

      <main className={styles.mainContent}>
        <h1 className={styles.pageTitle}>Объекты</h1>

        {error && !isModalOpen && (
          <div className={styles.errorMessage}>{error}</div>
        )}
        {success && <div className={styles.successMessage}>{success}</div>}

        <div className={styles.controlsRow}>
          <button className={styles.addButton} onClick={handleAddNew}>
            + Добавить объект
          </button>
          <div className={styles.filterContainer}>
            <input
              type="text"
              className={styles.filterInput}
              placeholder="Поиск по названию объекта..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
          </div>
          <div className={styles.statusFilterContainer}>
            <div className={styles.checkboxContainer}>
              <input
                className={styles.checkboxInput}
                type="checkbox"
                id="filterShowInactiveOnly"
                checked={filterShowInactiveOnly}
                onChange={(e) => setFilterShowInactiveOnly(e.target.checked)}
              />
              <label
                className={styles.checkboxLabel}
                htmlFor="filterShowInactiveOnly"
              >
                Неактивные
              </label>
            </div>
          </div>
        </div>

        <table className={styles.table}>
          <thead className={styles.tableHeader}>
            <tr>
              <th className={styles.tableHeaderCell}>Название</th>
              <th className={styles.tableHeaderCell}>Этаж</th>
              <th className={styles.tableHeaderCell}>Категория</th>
              <th className={styles.tableHeaderCell}>Расположение</th>
              <th className={styles.tableHeaderCell}>Статус</th>
              <th className={styles.tableHeaderCell}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {shops
              ?.filter((shop) => {
                const matchesName = shop.nameRu
                  .toLowerCase()
                  .includes(filterName.toLowerCase().trim());
                const matchesActiveStatus = filterShowInactiveOnly
                  ? !shop.isActive
                  : shop.isActive;
                return matchesName && matchesActiveStatus;
              })
              .sort((a, b) => a.nameRu.localeCompare(b.nameRu, "ru"))
              .map((shop) => (
                <tr key={shop.id} className={styles.tableRow}>
                  <td className={styles.tableCell}>{shop.nameRu}</td>
                  <td className={styles.tableCell}>{shop.floor}</td>
                  <td className={styles.tableCell}>
                    {shop.categories && shop.categories.length > 0
                      ? shop.categories.map((cat) => cat.nameRu).join(", ")
                      : shop.category?.nameRu || "-"}
                  </td>
                  <td className={styles.tableCell}>{shop.locationRu || "-"}</td>
                  <td className={styles.tableCell}>
                    <span
                      className={`${styles.badge} ${
                        shop.isActive
                          ? styles.badgeActive
                          : styles.badgeInactive
                      }`}
                    >
                      {shop.isActive ? "Активен" : "Неактивен"}
                    </span>
                  </td>
                  <td className={styles.tableCell}>
                    <button
                      className={styles.actionButton}
                      onClick={() => handleEdit(shop)}
                    >
                      Редактировать
                    </button>
                    <button
                      className={`${styles.actionButton} ${styles.actionDanger}`}
                      onClick={() => handleDelete(shop.id)}
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        <div
          className={`${styles.modal} ${isModalOpen ? styles.modalOpen : ""}`}
        >
          <div
            ref={modalContentRef}
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className={styles.modalTitle}>
              {editingShop ? "Редактировать объект" : "Добавить объект"}
            </h2>

            {error && (
              <div
                className={styles.errorMessage}
                style={{ whiteSpace: "pre-line" }}
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Название (RU) *</label>
                <input
                  className={styles.input}
                  type="text"
                  value={formData.nameRu}
                  onChange={(e) =>
                    setFormData({ ...formData, nameRu: e.target.value })
                  }
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Название (EN)</label>
                <input
                  className={styles.input}
                  type="text"
                  value={formData.nameEn}
                  onChange={(e) =>
                    setFormData({ ...formData, nameEn: e.target.value })
                  }
                />
              </div>

              <div className={styles.flexContainer}>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Этаж *</label>
                    <input
                      className={styles.input}
                      type="number"
                      value={formData.floor}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          return; // Don't update if empty
                        }
                        const parsed = parseInt(value, 10);
                        if (!isNaN(parsed)) {
                          setFormData({
                            ...formData,
                            floor: parsed,
                          });
                        }
                      }}
                      required
                    />
                  </div>
                </div>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Вкладка отображения</label>
                    <select
                      className={styles.select}
                      value={formData.tabId ?? ""}
                      onChange={(e) => {
                        const newTabId = e.target.value
                          ? parseInt(e.target.value, 10)
                          : null;
                        setFormData({
                          ...formData,
                          tabId: newTabId,
                        });
                        setSelectedTabId(newTabId ?? 2);
                      }}
                    >
                      <option value="">—</option>
                      {tabs
                        ?.filter((tab) => tab.isEditable === true)
                        .map((tab) => (
                          <option key={tab.id} value={tab.id}>
                            {tab.name?.ru ||
                              tab.name?.en ||
                              String(tab.id || "")}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Категории *</label>
                    <select
                      className={styles.select}
                      multiple
                      value={formData.categoryIds}
                      onChange={(e) => {
                        const selectedIds = Array.from(
                          e.target.selectedOptions,
                          (option) => option.value
                        );
                        setFormData({ ...formData, categoryIds: selectedIds });
                      }}
                      style={{ minHeight: "100px" }}
                    >
                      {categories
                        ?.slice()
                        .sort((a, b) => a.nameRu.localeCompare(b.nameRu, "ru"))
                        .map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.nameRu}
                          </option>
                        ))}
                    </select>
                    <small
                      style={{
                        display: "block",
                        marginTop: "5px",
                        color: "#666",
                      }}
                    >
                      Удерживайте Ctrl (или Cmd на Mac) для выбора нескольких
                      категорий
                    </small>
                  </div>
                </div>
              </div>

              <h3
                style={{
                  marginTop: "30px",
                  marginBottom: "15px",
                  color: "#333",
                }}
              >
                Фотографии объекта
              </h3>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Добавить фото (можно несколько)
                </label>
                <input
                  ref={shopPhotosInputRef}
                  className={styles.fileInput}
                  type="file"
                  id="shopPhotosUpload"
                  accept="image/*"
                  multiple
                  onChange={handleShopPhotosChange}
                  disabled={isUploadingPhotos}
                />
                <label
                  className={styles.fileInputLabel}
                  htmlFor="shopPhotosUpload"
                >
                  {isUploadingPhotos ? "Загрузка..." : "Выбрать файлы"}
                </label>
                {isUploadingPhotos && <p>Загрузка...</p>}
              </div>
              {shopPhotoPreviews.length > 0 && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Предпросмотр новых фотографий
                  </label>
                  <div className={styles.photoPreviewGrid}>
                    {shopPhotoPreviews.map((preview, index) => (
                      <div key={index} className={styles.photoPreviewItem}>
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className={styles.previewImage}
                        />
                        <button
                          type="button"
                          className={`${styles.actionButton} ${styles.actionDanger}`}
                          onClick={() => handleDeleteNewPhoto(index)}
                        >
                          Удалить
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {existingPhotos.length > 0 && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Существующие фотографии
                  </label>
                  <div className={styles.photoPreviewGrid}>
                    {existingPhotos.map((photo) => (
                      <div key={photo.id} className={styles.photoPreviewItem}>
                        <img
                          src={`${process.env.REACT_APP_MEDIA_PATH}${photo.url}`}
                          alt="Shop photo"
                          className={styles.previewImage}
                        />
                        <button
                          type="button"
                          className={`${styles.actionButton} ${styles.actionDanger}`}
                          onClick={() => handleDeletePhoto(photo.id)}
                        >
                          Удалить
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={styles.flexContainer}>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Расположение (RU)</label>
                    <input
                      className={styles.input}
                      type="text"
                      value={formData.locationRu}
                      onChange={(e) =>
                        setFormData({ ...formData, locationRu: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Расположение (EN)</label>
                    <input
                      className={styles.input}
                      type="text"
                      value={formData.locationEn}
                      onChange={(e) =>
                        setFormData({ ...formData, locationEn: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className={styles.flexContainer}>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Часы работы (RU) *</label>
                    <textarea
                      className={styles.textArea}
                      value={formData.workingHoursRu}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          workingHoursRu: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Часы работы (EN)</label>
                    <textarea
                      className={styles.textArea}
                      value={formData.workingHoursEn}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          workingHoursEn: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Краткое описание (RU)</label>
                <textarea
                  className={styles.textArea}
                  value={formData.shortDescriptionRu}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      shortDescriptionRu: e.target.value,
                    })
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Краткое описание (EN)</label>
                <textarea
                  className={styles.textArea}
                  value={formData.shortDescriptionEn}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      shortDescriptionEn: e.target.value,
                    })
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Полное описание (RU)</label>
                <textarea
                  className={styles.textArea}
                  value={formData.fullDescriptionRu}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      fullDescriptionRu: e.target.value,
                    })
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Полное описание (EN)</label>
                <textarea
                  className={styles.textArea}
                  value={formData.fullDescriptionEn}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      fullDescriptionEn: e.target.value,
                    })
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Ключевые слова для поиска
                </label>
                <input
                  className={styles.input}
                  type="text"
                  value={formData.searchKeywords}
                  onChange={(e) =>
                    setFormData({ ...formData, searchKeywords: e.target.value })
                  }
                  placeholder="Разделяйте запятой"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Логотип объекта</label>
                <div
                  className={`${styles.dropZone} ${
                    isDraggingLogo ? styles.dropZoneActive : ""
                  }`}
                  onDragOver={handleLogoDragOver}
                  onDragLeave={handleLogoDragLeave}
                  onDrop={handleLogoDrop}
                >
                  <input
                    ref={logoInputRef}
                    className={styles.fileInput}
                    type="file"
                    id="logoUpload"
                    accept="image/*"
                    onChange={handleLogoChange}
                    disabled={isUploadingLogo}
                  />
                  <label className={styles.fileInputLabel} htmlFor="logoUpload">
                    {isUploadingLogo ? "Загрузка..." : "Выбрать файл"}
                  </label>
                  <div className={styles.dropZoneText}>
                    или перетащите файл сюда
                  </div>
                  {logoPreview && (
                    <div className={styles.logoPreview}>
                      <img src={logoPreview} alt="Logo preview" />
                    </div>
                  )}
                  {!logoPreview && formData.logoUrl && (
                    <>
                      <div className={styles.fileInfo}>Логотип уже загружен</div>
                      <div className={styles.logoPreview}>
                        <img
                          src={`${process.env.REACT_APP_MEDIA_PATH}${formData.logoUrl}`}
                          alt="Current logo"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <h3
                style={{
                  marginTop: "30px",
                  marginBottom: "15px",
                  color: "#333",
                }}
              >
                Социальные сети
              </h3>
              <div className={styles.flexContainer}>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Instagram</label>
                    <input
                      className={styles.input}
                      type="text"
                      value={formData.instagram}
                      onChange={(e) =>
                        setFormData({ ...formData, instagram: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Facebook</label>
                    <input
                      className={styles.input}
                      type="text"
                      value={formData.facebook}
                      onChange={(e) =>
                        setFormData({ ...formData, facebook: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className={styles.flexContainer}>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Twitter</label>
                    <input
                      className={styles.input}
                      type="text"
                      value={formData.twitter}
                      onChange={(e) =>
                        setFormData({ ...formData, twitter: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>TikTok</label>
                    <input
                      className={styles.input}
                      type="text"
                      value={formData.tiktok}
                      onChange={(e) =>
                        setFormData({ ...formData, tiktok: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className={styles.flexContainer}>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>YouTube</label>
                    <input
                      className={styles.input}
                      type="text"
                      value={formData.youtube}
                      onChange={(e) =>
                        setFormData({ ...formData, youtube: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Telegram</label>
                    <input
                      className={styles.input}
                      type="text"
                      value={formData.telegram}
                      onChange={(e) =>
                        setFormData({ ...formData, telegram: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>

              <h3
                style={{
                  marginTop: "30px",
                  marginBottom: "15px",
                  color: "#333",
                }}
              >
                Параметры карты
              </h3>
              <div className={styles.flexContainer}>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Иконка неактивного объекта *
                    </label>
                    <div
                      className={`${styles.dropZone} ${
                        isDraggingInactiveIcon ? styles.dropZoneActive : ""
                      }`}
                      onDragOver={handleInactiveIconDragOver}
                      onDragLeave={handleInactiveIconDragLeave}
                      onDrop={handleInactiveIconDrop}
                    >
                      <input
                        ref={mapIconInactiveInputRef}
                        className={styles.fileInput}
                        type="file"
                        id="mapIconInactiveUpload"
                        accept="image/*"
                        onChange={handleMapIconInactiveChange}
                        disabled={isUploadingInactiveIcon}
                      />
                      <label
                        className={styles.fileInputLabel}
                        htmlFor="mapIconInactiveUpload"
                      >
                        {isUploadingInactiveIcon ? "Загрузка..." : "Выбрать файл"}
                      </label>
                      <div className={styles.dropZoneText}>
                        или перетащите файл сюда
                      </div>
                      {mapIconInactivePreview && (
                        <div className={styles.logoPreview}>
                          <img
                            src={mapIconInactivePreview}
                            alt="Inactive icon preview"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Иконка активного объекта *
                    </label>
                    <div
                      className={`${styles.dropZone} ${
                        isDraggingActiveIcon ? styles.dropZoneActive : ""
                      }`}
                      onDragOver={handleActiveIconDragOver}
                      onDragLeave={handleActiveIconDragLeave}
                      onDrop={handleActiveIconDrop}
                    >
                      <input
                        ref={mapIconActiveInputRef}
                        className={styles.fileInput}
                        type="file"
                        id="mapIconActiveUpload"
                        accept="image/*"
                        onChange={handleMapIconActiveChange}
                        disabled={isUploadingActiveIcon}
                      />
                      <label
                        className={styles.fileInputLabel}
                        htmlFor="mapIconActiveUpload"
                      >
                        {isUploadingActiveIcon ? "Загрузка..." : "Выбрать файл"}
                      </label>
                      <div className={styles.dropZoneText}>
                        или перетащите файл сюда
                      </div>
                      {mapIconActivePreview && (
                        <div className={styles.logoPreview}>
                          <img
                            src={mapIconActivePreview}
                            alt="Active icon preview"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Interactive floor map positioner */}
              {(() => {
                const selectedFloor = floors?.find(
                  (f) => f.number === formData.floor
                );
                const mediaBase = process.env.REACT_APP_MEDIA_PATH;
                const floorMapUrl = selectedFloor?.mapUrl
                  ? selectedFloor.mapUrl.startsWith("http")
                    ? selectedFloor.mapUrl
                    : `${mediaBase}${
                        selectedFloor.mapUrl.startsWith("/") ? "" : "/"
                      }${selectedFloor.mapUrl}`
                  : "";

                return floorMapUrl ? (
                  <div className={styles.formGroup}>
                    <label
                      className={styles.label}
                      style={{
                        marginBottom: "15px",
                        fontSize: "16px",
                        fontWeight: "bold",
                      }}
                    >
                      Расположение на карте этажа
                    </label>
                    <ShopPositioner2D
                      key={shopPositionerKey}
                      floorMapUrl={floorMapUrl}
                      shopIconUrl={
                        mapIconActivePreview ||
                        (formData.mapIconActiveUrl &&
                        formData.mapIconActiveUrl.trim()
                          ? `${process.env.REACT_APP_MEDIA_PATH}${formData.mapIconActiveUrl}`
                          : undefined)
                      }
                      initialPositionX={formData.positionX}
                      initialPositionY={formData.positionY}
                      initialPositionZ={formData.positionZ}
                      initialScaleX={formData.scaleX}
                      initialScaleY={formData.scaleY}
                      onPositionChange={(pos) => {
                        setFormData({
                          ...formData,
                          positionX: pos.x,
                          positionY: pos.y,
                          positionZ: pos.z,
                          scaleX: pos.scaleX,
                          scaleY: pos.scaleY,
                        });
                      }}
                    />
                  </div>
                ) : null;
              })()}

              <h4
                style={{
                  marginTop: "20px",
                  marginBottom: "10px",
                  color: "#333",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                Или установите координаты вручную:
              </h4>

              <div className={styles.flexContainer}>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Масштаб X</label>
                    <input
                      className={styles.input}
                      type="number"
                      step="0.01"
                      value={formData.scaleX}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          scaleX: parseFloat(e.target.value) || 1,
                        })
                      }
                    />
                  </div>
                </div>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Масштаб Y</label>
                    <input
                      className={styles.input}
                      type="number"
                      step="0.01"
                      value={formData.scaleY}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          scaleY: parseFloat(e.target.value) || 1,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className={styles.flexContainer}>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Позиция X</label>
                    <input
                      className={styles.input}
                      type="number"
                      step="0.01"
                      value={formData.positionX}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          positionX: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                </div>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Позиция Y</label>
                    <input
                      className={styles.input}
                      type="number"
                      step="0.01"
                      value={formData.positionY}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          positionY: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Позиция Z</label>
                <input
                  className={styles.input}
                  type="number"
                  step="0.01"
                  value={formData.positionZ}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      positionZ: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <div className={styles.checkboxContainer}>
                  <input
                    className={styles.checkboxInput}
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                  <label className={styles.checkboxLabel} htmlFor="isActive">
                    Объект активен
                  </label>
                </div>
              </div>

              <div className={styles.modalActions}>
                <button
                  className={`${styles.actionButton} ${styles.actionWarning}`}
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setError("");
                  }}
                >
                  Отмена
                </button>
                <button
                  className={styles.actionButton}
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Сохранение..."
                    : editingShop
                    ? "Обновить"
                    : "Создать"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
